import { Component, Input, OnInit } from "@angular/core";
import { Rodeo } from "../../../../../model/rodeo";
import { PointStatsApiService } from "../../../../../services/points/pointStats.api.service";
import { PointStatListItem, EditedPointStats } from "../../../../../lib/types/PointStatListItem";
import { RodeoEvent } from "../../../../../model/rodeoEvent";
import { PayoutConfig } from "../../../../../model/payoutConfig";
import { LoadingController, ModalController, NavController, NavParams } from "ionic-angular";
import { PointSeries } from '../../../../../model/point-series';
import { getRunSecondValue, groupBy } from '../../../../../lib/helpers';
import { PointStatsLogsComponent } from "../point-stats-logs/point-stats-logs.component";
import { PointStatsReportComponent } from "../../reports/points/point-stats-report.component";

@Component({
    selector: 'staff-pointstats',
    templateUrl: 'point-stats.html',
})
export class PointStatsPage implements OnInit {
    _viewMode: string = 'pointStats';
    pointStats: PointStatListItem[];
    selectedRodeoEvent: RodeoEvent;
    selectedPayout: PayoutConfig;
    payoutSelections: PayoutConfig[];
    groupedPointStats: { [id: string]: PointStatListItem[] } = {};
    assignedPointSeries: PointSeries[];
    selectedPointSeries: PointSeries;
    editingMode: boolean = false;
    editedPointStats: EditedPointStats[] = [];

    @Input() rodeo: Rodeo;

    constructor(
        private pointStatsApiService: PointStatsApiService,
        private loadingController: LoadingController,
        private modalController: ModalController,
        private navController: NavController,
    ) { }

    async ngOnInit(): Promise<void> {
        this.selectedRodeoEvent = this.rodeo.rodeoEvents[0];
        this.updatePayouts();
        this.loadPointSeries();
        await this.reloadPointStats();
    }

    loadPointSeries() {
        const useAllPayout = this.selectedPayout.id === 0;

        const allPointSeriesInPayouts = useAllPayout ? this.rodeo.payoutConfigs.flatMap(pc => pc.pointSeries) : this.selectedPayout.pointSeries;
        const pointSeriesMap = new Map(allPointSeriesInPayouts.map<[any, any]>(item =>
            [item['id'], item]))
        const distinctPointSeries = Array.from<PointSeries>(pointSeriesMap.values());

        this.assignedPointSeries = distinctPointSeries;
        this.selectedPointSeries = this.assignedPointSeries[0];
    }

    onSelectedPayoutChange() {
        this.loadPointSeries();
        this.reloadPointStats();
    }

    idOptionSorter(a, b) {
        return a.id == b.id;
    }

    matchSelections(a, b) {
        return a.name == b.name;
    }

    startEditingMode() {
        this.editingMode = true;
    }

    cancelEditingMode() {
        this.editedPointStats = this.editedPointStats.filter(x => x.commited);
        this.refreshGroupedPointStats();
        this.editingMode = false;
    }

    async saveChanges() {
        const loading = this.loadingController.create({
            content: 'Saving point stats'
        })
        loading.present();
        const editedPointStats = this.editedPointStats.filter(p => !p.commited);
        await this.pointStatsApiService.editPointStats(editedPointStats);
        loading.dismiss();

        this.editedPointStats = this.editedPointStats.map(x => ({ ...x, commited: true }));
        this.reloadPointStats();
        this.editingMode = false;
    }

    refreshGroupedPointStats() {
        const result = this.getPointStatsEdited();
        this.groupedPointStats = groupBy(result, ({ leaderCategory }) => leaderCategory);
    }

    objectKeys = Object.keys;

    async rodeoEventChange() {
        this.updatePayouts();
        await this.reloadPointStats();
    }

    updatePayouts() {
        const rodeoEventPayoutConfigs = this.rodeo.payoutConfigs.filter(pg => pg.sources.some(s => s.rodeoEventId === this.selectedRodeoEvent.id));
        this.payoutSelections = [{ name: 'All', id: 0 } as PayoutConfig, ...rodeoEventPayoutConfigs];
        this.selectedPayout = this.payoutSelections[0];
    }

    async reloadPointStats() {
        const rodeoEventId = this.selectedRodeoEvent.id;
        const payoutConfigId = this.selectedPayout.id;
        const pointSeriesId = this.selectedPointSeries ? this.selectedPointSeries.id : 0;
        const loading = this.loadingController.create({
            content: 'Loading point stats'
        })
        loading.present();
        this.pointStats = await this.pointStatsApiService
            .getRodeoPointStats(
                this.rodeo.id,
                rodeoEventId,
                0,
                payoutConfigId,
                pointSeriesId,
            );
        this.editedPointStats = [];
        this.refreshGroupedPointStats();

        loading.dismiss();
    }

    editPointStats(evt, pointStat: PointStatListItem, isRider2Points: boolean) {
        const newPointValue = Number(evt.target.value);
        let existingEditedPoint = this.editedPointStats.find(ps => ps.pointStatId === pointStat.id && ps.isRider2Points === isRider2Points);
        if (existingEditedPoint) {
            existingEditedPoint.value = newPointValue;
            existingEditedPoint.commited = false;
        } else {
            this.editedPointStats.push({
                commited: false,
                pointStatId: pointStat.id,
                value: newPointValue,
                isRider2Points: isRider2Points,
            })
        }

        this.refreshGroupedPointStats();
    }

    getPointStatsEdited() {
        const result = this.pointStats.map(ps => {
            const edited = this.editedPointStats.find(eps => eps.pointStatId === ps.id && !eps.isRider2Points);
            const editedRider2 = this.editedPointStats.find(eps => eps.pointStatId === ps.id && eps.isRider2Points);
            if (edited || editedRider2) {
                return {
                    ...ps,
                    edited: {
                        rider1: edited,
                        rider2: editedRider2
                    }
                }
            }
            return {
                ...ps,
                edited: {

                }
            }
        });
        return result;
    }

    showLogs(pointStat: PointStatListItem) {
        let profileModal = this.modalController.create(PointStatsLogsComponent, { pointStat: pointStat });
        profileModal.present();
    }

    print() {
        this.navController.push(PointStatsReportComponent, {
            rodeo: this.rodeo,
            selectedRodeoEvent: this.selectedRodeoEvent,
            selectedPayout: this.selectedPayout,
            selectedPointSeries: this.selectedPointSeries,
            print: true,
        });
    }

    getSecondValue = getRunSecondValue;

    get isTeamEventType() {
        return this.selectedRodeoEvent.configuration.entries.showContestantTwo;
    }

    get firstLabel() {
        return this.selectedRodeoEvent.configuration.runs.slotOneLabel;
    }

    get secondLabel() {
        return this.selectedRodeoEvent.configuration.runs.slotTwoLabel;
    }

}


// WEBPACK FOOTER //
// ./src/pages/staff/rodeo/results/points/point-stats.ts